import type { FrameBlockModel } from '@blocksuite/affine-model';
import { createIdentifier } from '@blocksuite/global/di';
import type { ExtensionType } from '@blocksuite/store';
import { Subject } from 'rxjs';
export const EdgelessLegacySlotIdentifier = createIdentifier<{
  readonlyUpdated: Subject<boolean>;
  navigatorSettingUpdated: Subject<{
    hideToolbar?: boolean;
    blackBackground?: boolean;
    fillScreen?: boolean;
    showFramesOnCanvas?: boolean;
    framePanelOpen?: boolean;
  }>;
  navigatorFrameChanged: Subject<FrameBlockModel>;
  /**
   * @deprecated Use frameIndexChanged instead (sl-2vda SOLID refactoring)
   * Set the current frame index externally (for follower mode).
   * Used by collaborative presentation to sync frame navigation.
   */
  navigatorSetFrame: Subject<number>;
  /**
   * @deprecated Use frameIndexChanged instead (sl-2vda SOLID refactoring)
   * Set the initial frame index when presentation starts (for BOTH moderator and follower).
   * SOLID: Allows external control of initial frame without _isFollowerMode restriction.
   * Used to start presentation from the frame the user was viewing, not always frame 0.
   */
  navigatorSetInitialFrame: Subject<number>;
  /**
   * sl-2vda SOLID: Input slot - Set frame index from external source (Service → Toolbar)
   * The PresentationToolbar is now "dumb" - it reads frame index from this slot.
   * Single Source of Truth: PresentationFrameStateService owns the frame index.
   */
  frameIndexChanged: Subject<number>;
  /**
   * sl-2vda SOLID: Output slot - User requested frame change (Toolbar → Service)
   * When user clicks Next/Prev, toolbar emits this event instead of changing internal state.
   * The service handles the actual state change and broadcasts via frameIndexChanged.
   */
  userRequestedFrameChange: Subject<number>;
  /**
   * sl-2vda: Start presentation from a specific frame (Frame Panel → Editor)
   * Triggered by right-click → "Start presentation from here" in Frame Panel.
   */
  startPresentationFromFrame: Subject<{ frameIndex: number; frameId: string }>;
  /**
   * sl-2vda: Join presentation at specific frame (Follower UI → Editor)
   * USES THE SAME CODE PATH as startPresentationFromFrame!
   * The only difference is joinFrameMode() instead of enterFrameMode().
   */
  joinPresentationAtFrame: Subject<{ frameIndex: number; frameId: string }>;
  fullScreenToggled: Subject<void>;

  elementResizeStart: Subject<void>;
  elementResizeEnd: Subject<void>;
  toggleNoteSlicer: Subject<void>;

  toolbarLocked: Subject<boolean>;
}>('AffineEdgelessLegacySlotService');

export const EdgelessLegacySlotExtension: ExtensionType = {
  setup: di => {
    di.addImpl(EdgelessLegacySlotIdentifier, () => ({
      readonlyUpdated: new Subject<boolean>(),
      navigatorSettingUpdated: new Subject<{
        hideToolbar?: boolean;
        blackBackground?: boolean;
        fillScreen?: boolean;
        showFramesOnCanvas?: boolean;
        framePanelOpen?: boolean;
      }>(),
      navigatorFrameChanged: new Subject<FrameBlockModel>(),
      navigatorSetFrame: new Subject<number>(),
      navigatorSetInitialFrame: new Subject<number>(),
      // sl-2vda SOLID: New slots for dumb toolbar
      frameIndexChanged: new Subject<number>(),
      userRequestedFrameChange: new Subject<number>(),
      startPresentationFromFrame: new Subject<{
        frameIndex: number;
        frameId: string;
      }>(),
      joinPresentationAtFrame: new Subject<{
        frameIndex: number;
        frameId: string;
      }>(),
      fullScreenToggled: new Subject(),
      elementResizeStart: new Subject(),
      elementResizeEnd: new Subject(),
      toggleNoteSlicer: new Subject(),
      toolbarLocked: new Subject<boolean>(),
    }));
  },
};
